// How steep the track was along it, and how fast height was gained and lost, from the heights alone
// and, when there are times, the moving-time timeline.

import { stretchCount, sum, valueAt } from './timeline';

/** Point to point, the terrain model's steps read as cliffs, so steepness is read over this much track. */
export const GRADIENT_WINDOW_METERS = 100;

/** Gentler than this either way is walking on the level, not climbing or descending. */
export const CLIMBING_GRADIENT_PERCENT = 3;

export const HIGHLIGHT_METERS = 300;

/** Below this, a few minutes of climbing would give a rate nobody could sustain. */
const SHORTEST_RATED_SECONDS = 60;

export interface AscentSample {
  /** Metres along the track. */
  distance: number;
  /** Percent, negative going down. */
  gradient: number;
  /** Metres an hour, negative going down; null without times. */
  verticalSpeed: number | null;
}

export interface AscentRate {
  seconds: number;
  metersPerHour: number;
}

/** In metres along the track. */
export interface AscentHighlight {
  from: number;
  to: number;
  gradient: number;
  /** The fastest climb with times; without them, the steepest section and null. */
  metersPerHour: number | null;
}

export interface AscentProfile {
  samples: AscentSample[];
  maxGradient: number;
  climbing: AscentRate | null;
  descending: AscentRate | null;
  highlight: AscentHighlight | null;
}

/**
 * `elapsedMs` runs alongside `distances`. A continued recording's track jumps at each of
 * `segmentStartIndices` between legs: that ground is drawn across but was never walked, so it
 * counts towards no rate, speed or highlight.
 */
export function ascentProfile(
  distances: number[],
  altitudes: Array<number | null | undefined>,
  elapsedMs: number[] | null,
  segmentStartIndices: number[] = []
): AscentProfile | null {
  const total = distances[distances.length - 1];
  if (!(total > 0) || distances.length !== altitudes.length) return null;
  const known = distances.map((_, i) => i).filter((i) => typeof altitudes[i] === 'number' && Number.isFinite(altitudes[i]));
  if (known.length < 2) return null;

  const heightDistances = known.map((i) => distances[i]);
  const heights = known.map((i) => altitudes[i] as number);
  const heightAt = (distance: number) => valueAt(distance, heightDistances, heights);
  const timed = elapsedMs && elapsedMs.length === distances.length ? elapsedMs : null;
  const secondsAt = (distance: number) => (timed ? valueAt(distance, distances, timed) / 1000 : 0);
  const walked = walkedParts(legJumps(distances, segmentStartIndices));

  const over = (from: number, to: number): Reading => {
    const parts = walked(from, to);
    const meters = sum(parts.map(([a, b]) => b - a));
    const rise = sum(parts.map(([a, b]) => heightAt(b) - heightAt(a)));
    const seconds = secondsAt(to) - secondsAt(from);
    const onFoot = meters >= (to - from) / 2;
    return {
      gradient: ((heightAt(to) - heightAt(from)) / (to - from)) * 100,
      walkedGradient: onFoot ? (rise / meters) * 100 : null,
      rise,
      seconds,
      crossesJoin: meters < to - from - 1e-6,
      metersPerHour: timed && onFoot && seconds > 0 ? (rise / seconds) * 3600 : null,
    };
  };

  const count = stretchCount(total);
  const length = total / count;
  const reach = Math.max(GRADIENT_WINDOW_METERS, length) / 2;
  const windows = Array.from({ length: count }, (_, i) => {
    const distance = (i + 0.5) * length;
    return { distance, reading: over(Math.max(distance - reach, 0), Math.min(distance + reach, total)) };
  });

  let climbed = { meters: 0, seconds: 0 };
  let descended = { meters: 0, seconds: 0 };
  if (timed) {
    windows.forEach(({ reading }, i) => {
      const gradient = reading.walkedGradient;
      if (gradient === null) return;
      const { rise, seconds } = over(i * length, (i + 1) * length);
      if (gradient >= CLIMBING_GRADIENT_PERCENT) climbed = { meters: climbed.meters + rise, seconds: climbed.seconds + seconds };
      else if (gradient <= -CLIMBING_GRADIENT_PERCENT) descended = { meters: descended.meters - rise, seconds: descended.seconds + seconds };
    });
  }
  const rate = ({ meters, seconds }: { meters: number; seconds: number }): AscentRate | null =>
    seconds >= SHORTEST_RATED_SECONDS && meters > 0 ? { seconds, metersPerHour: meters / (seconds / 3600) } : null;

  return {
    samples: windows.map(({ distance, reading }) => ({
      distance,
      gradient: reading.gradient,
      verticalSpeed: reading.metersPerHour,
    })),
    maxGradient: Math.max(0, ...windows.map(({ reading }) => reading.walkedGradient ?? 0)),
    climbing: rate(climbed),
    descending: rate(descended),
    highlight: highlightOf(total, length, timed !== null, over),
  };
}

interface Reading {
  /** Straight across any join, as the elevation profile is drawn. */
  gradient: number;
  /** Over the ground walked; null when most of it is a join. */
  walkedGradient: number | null;
  rise: number;
  seconds: number;
  crossesJoin: boolean;
  metersPerHour: number | null;
}

/** Where the track jumps between legs, in metres along it. */
function legJumps(distances: number[], segmentStartIndices: number[]): Array<[number, number]> {
  return segmentStartIndices
    .filter((i) => i > 0 && i < distances.length && distances[i] > distances[i - 1])
    .map((i) => [distances[i - 1], distances[i]]);
}

/** The parts of from–to that were walked rather than jumped. */
function walkedParts(jumps: Array<[number, number]>) {
  return (from: number, to: number): Array<[number, number]> =>
    jumps.reduce<Array<[number, number]>>(
      (parts, [start, end]) =>
        parts.flatMap(([a, b]): Array<[number, number]> =>
          end <= a || start >= b
            ? [[a, b]]
            : ([[a, Math.min(b, start)], [Math.max(a, end), b]] as Array<[number, number]>).filter(([x, y]) => y > x)
        ),
      [[from, to]]
    );
}

/** The fastest climb over HIGHLIGHT_METERS with times, or the steepest one without; never across a join. */
function highlightOf(total: number, step: number, timed: boolean, over: (from: number, to: number) => Reading): AscentHighlight | null {
  const span = Math.min(HIGHLIGHT_METERS, total);
  let best: AscentHighlight | null = null;
  for (let i = 0; i * step + span <= total + 1e-6; i++) {
    const from = i * step;
    const to = Math.min(from + span, total);
    const { gradient, crossesJoin, metersPerHour } = over(from, to);
    if (crossesJoin || gradient < CLIMBING_GRADIENT_PERCENT) continue;
    if (timed && metersPerHour === null) continue;
    const better = timed ? metersPerHour! > (best?.metersPerHour ?? -Infinity) : gradient > (best?.gradient ?? -Infinity);
    if (better) best = { from, to, gradient, metersPerHour: timed ? metersPerHour : null };
  }
  return best;
}
